import type { PaymentsScopeParams } from "../../../../domain/models/PaymentsKpi";
import type {
  SearchFilter,
  SearchScope,
} from "../../../../domain/models/http/SearchRequest";
import type {
  MissingCardPaymentState,
  PaymentsDoneState,
  PaymentsSharedFilters,
} from "@/types/payments";

type RangeDateValue = { toDate: (timeZone: string) => Date } | null | undefined;

const toIsoDate = (value: RangeDateValue): string | undefined => {
  if (!value) {
    return undefined;
  }

  const date = value.toDate("UTC");
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const toScopeParams = (
  filters: PaymentsSharedFilters,
): PaymentsScopeParams => {
  const params: PaymentsScopeParams = {};

  if (filters.subsidiaryId) {
    params.subsidiary_id = filters.subsidiaryId;
  }

  if (filters.domaIds.length > 0) {
    params.doma_ids = filters.domaIds;
  }

  const startDate = toIsoDate(filters.dateRange?.start);
  const endDate = toIsoDate(filters.dateRange?.end);

  if (startDate) {
    params.start_date = startDate;
  }

  if (endDate) {
    params.end_date = endDate;
  }

  return params;
};

/**
 * Une colonne `date` doit être comparée sans heure : la comparaison est
 * lexicographique sur SQLite, où "2026-08-10" < "2026-08-10 00:00:00" — les
 * bornes horaires y écartent donc toutes les lignes du jour.
 */
export type PaymentsDateField = {
  field: string;
  kind: "date" | "datetime";
};

export const toDateFilters = (
  dateRange: PaymentsSharedFilters["dateRange"],
  { field, kind }: PaymentsDateField,
): SearchFilter[] => {
  const searchFilters: SearchFilter[] = [];
  const startIso = toIsoDate(dateRange?.start);
  const endIso = toIsoDate(dateRange?.end);
  const isDateOnly = kind === "date";

  if (startIso) {
    searchFilters.push({
      field,
      operator: ">=",
      value: isDateOnly ? startIso : `${startIso} 00:00:00`,
    });
  }

  if (endIso) {
    searchFilters.push({
      field,
      operator: "<=",
      value: isDateOnly ? endIso : `${endIso} 23:59:59`,
    });
  }

  return searchFilters;
};

export const toTableFilters = (
  filters: PaymentsSharedFilters,
  dateField: PaymentsDateField,
): SearchFilter[] => {
  const searchFilters: SearchFilter[] = [];

  if (filters.subsidiaryId) {
    searchFilters.push({
      field: "subsidiary_id",
      operator: "=",
      value: filters.subsidiaryId,
    });
  }

  if (filters.domaIds.length > 0) {
    searchFilters.push({
      field: "doma_id",
      operator: "in",
      value: filters.domaIds,
    });
  }

  return [...searchFilters, ...toDateFilters(filters.dateRange, dateField)];
};

/**
 * L'écran filtre sur « FAIT », la base ne connaît que `to_do` : la valeur est
 * retournée au passage. `all` ne pose aucun filtre.
 */
export const toDealershipFilters = (
  dealerships: readonly string[],
): SearchFilter[] =>
  dealerships.length === 0
    ? []
    : [
        {
          field: "bankAccount.dealership",
          operator: "in",
          value: [...dealerships],
        },
      ];

export const toDoneFilters = (done: PaymentsDoneState): SearchFilter[] => {
  if (done === "all") {
    return [];
  }

  return [{ field: "to_do", operator: "=", value: done === "no" }];
};

const AMOUNT_PATTERN = /^-?\d+(\.\d*)?$/;

/**
 * Le montant est saisi au format affiché (« 1 234,50 € ») mais stocké en
 * décimal : sans normalisation, aucune ligne ne correspond.
 */
export const toAmountSearchValue = (search: string): string | null => {
  const value = search.replace(/[\s\u00a0\u202f€]/g, "").replace(",", ".");

  return AMOUNT_PATTERN.test(value) ? value : null;
};

const toLikeFilter = (field: string, term: string): SearchFilter => ({
  field,
  operator: "like",
  value: `%${term}%`,
  type: "or",
});

export const toSearchFilter = (
  search: string,
  fields: readonly string[],
  amountFields: readonly string[] = [],
): SearchFilter | null => {
  const value = search.trim();

  if (!value) {
    return null;
  }

  const amountValue = toAmountSearchValue(value);
  const nested: SearchFilter[] = [
    ...fields.map((field) => toLikeFilter(field, value)),
    ...(amountValue === null
      ? []
      : amountFields.map((field) => toLikeFilter(field, amountValue))),
  ];

  if (nested.length === 0) {
    return null;
  }

  return { nested } as SearchFilter;
};

export const getSharedFiltersSignature = (
  filters: PaymentsSharedFilters,
): string =>
  [
    filters.subsidiaryId ?? "",
    [...filters.domaIds].sort().join(","),
    toIsoDate(filters.dateRange?.start) ?? "",
    toIsoDate(filters.dateRange?.end) ?? "",
  ].join("|");

export const stateToScopes = (
  state: MissingCardPaymentState,
): SearchScope[] => {
  if (state === "archived") {
    return [{ name: "onlyArchived" }];
  }

  if (state === "all") {
    return [{ name: "withArchived" }];
  }

  return [];
};

/**
 * Les virements nationaux ne portent pas de filiale : le scope la retrouve par
 * ses plaques. Ils n'ont pas de site, le filtre par site ne s'y applique pas.
 */
export const toSubsidiaryScopes = (
  subsidiaryId: string | null,
): SearchScope[] =>
  subsidiaryId ? [{ name: "ofSubsidiary", parameters: [subsidiaryId] }] : [];
